import { useEffect, useState } from "react";
import {
  Container,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Spinner,
  Alert,
  Badge,
  Row,
  Col,
  Tabs,
  Tab,
} from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import Select from "react-select";
// import { FaDownload, FaTrash, FaTrashRestore, FaBan } from "react-icons/fa6";
import { FaDownload, FaTrash, FaBan } from "react-icons/fa6";
import { GrView } from "react-icons/gr";
import { FaUsersViewfinder } from "react-icons/fa6";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import {
  uploadHrOrgDocument,
  downloadHrOrgDocument,
  getAllHrDocumentTypes,
  saveHrOrgDocumentVisibility,
  getVisibilityByDocumentId,
  getVisibleHrOrgDocuments,
  viewHrOrgDocument,
  updateHrOrgDocument,
  getDocumentHistory,
  deleteHrOrgDocument,
  restoreHrOrgDocument,
  permanentDeleteHrOrgDocument,
  getDeletedHrOrgDocumentsByOrgId,
} from "../Api/HrOrgDocumentMasterApi";

import { getDepartmentsByOrgId } from "../Api/DepartmentOrgMaster";
import { getResourceByMavUserId } from "../Api/ResourceList";
import { getResourcesByOrgId } from "../Api/HrOrgDocumentMasterApi";

// ================= DEFAULT VISIBILITY =================
const defaultVisibility = {
  everyone: false,
  me: false,
  departments: [],
  users: [],
};

const ViewHrOrgDocuments = () => {
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const orgId = String(userDetails?.organisationId);
  const userId = String(userDetails?.userId);

  const [documents, setDocuments] = useState([]);
  const [deletedDocuments, setDeletedDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [documentTypeMap, setDocumentTypeMap] = useState({});
  const [departments, setDepartments] = useState([]);
  const [resources, setResources] = useState([]);
  const [userDepartmentIds, setUserDepartmentIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("active");

  const [showAddModal, setShowAddModal] = useState(false);
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyList, setHistoryList] = useState([]);

  const [formData, setFormData] = useState({
    documentTitle: "",
    documentType: "",
    file: null,
  });

  const [visibility, setVisibility] = useState(defaultVisibility);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [editFormData, setEditFormData] = useState({
    documentTitle: "",
    documentType: "",
    file: null,
  });

  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState("soft"); // 'soft' or 'permanent'

  // ================= INIT =================
  useEffect(() => {
    if (orgId && userId) {
      fetchUserResource();
      fetchDepartments();
      fetchDocumentTypes();
      fetchResources();
    }
  }, [orgId, userId]);

  useEffect(() => {
    if (activeTab === "active") {
      fetchDocuments();
    } else {
      fetchDeletedDocuments();
    }
  }, [userDepartmentIds, activeTab]);

  // ================= DATA FETCHING =================

  const fetchUserResource = async () => {
    try {
      const data = await getResourceByMavUserId(userId);
      const deptCode = data?.resource?.department;
      setUserDepartmentIds(deptCode ? [String(deptCode)] : []);
    } catch {
      setUserDepartmentIds([]);
    }
  };

  const fetchDepartments = async () => {
    const data = await getDepartmentsByOrgId(orgId);
    setDepartments(data || []);
  };

  const fetchResources = async () => {
    try {
      const data = await getResourcesByOrgId(orgId);
      setResources(data || []);
    } catch {
      toast.error("Failed to load users");
    }
  };

  const userOptions = resources.map((u) => ({
    value: u.resourceId,
    label: `${u.name} (${u.resourceId})`,
  }));

  const fetchDocumentTypes = async () => {
    const data = await getAllHrDocumentTypes();
    setDocumentTypes(data || []);

    const map = {};
    data.forEach((t) => (map[t.documentTypeId] = t.documentTypeName));
    setDocumentTypeMap(map);
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const data = await getVisibleHrOrgDocuments(
        orgId,
        userId,
        userDepartmentIds,
      );
      const latestDocs = getLatestVersions(data || []);
      setDocuments(latestDocs);
    } catch {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletedDocuments = async () => {
    try {
      setLoading(true);
      const data = await getDeletedHrOrgDocumentsByOrgId(orgId);
      const latestDeletedDocs = getLatestVersions(data || []);
      setDeletedDocuments(latestDeletedDocs);
    } catch {
      toast.error("Failed to load deleted documents");
    } finally {
      setLoading(false);
    }
  };

  const getLatestVersions = (docs) => {
    const grouped = {};

    docs.forEach((doc) => {
      const rootId = doc.parentDocumentId || doc.documentId;

      if (!grouped[rootId]) {
        grouped[rootId] = doc;
      } else if (doc.documentVersion > grouped[rootId].documentVersion) {
        grouped[rootId] = doc;
      }
    });

    return Object.values(grouped);
  };

  const openHistoryModal = async (doc) => {
    try {
      const data = await getDocumentHistory(
        doc.parentDocumentId || doc.documentId,
      );
      setHistoryList(data);
      setShowHistoryModal(true);
    } catch {
      toast.error("Failed to load history");
    }
  };

  // ================= DOCUMENT OPERATIONS =================

  const handleUpload = async () => {
    if (!formData.file) {
      toast.error("Please select a file");
      return;
    }

    const payload = new FormData();
    payload.append("DocumentTitle", formData.documentTitle);
    payload.append("DocumentType", formData.documentType);
    payload.append("OrgId", orgId);
    payload.append("CreatedBy", userId);
    payload.append("File", formData.file);

    try {
      await uploadHrOrgDocument(payload);
      toast.success("Document uploaded successfully");
      setShowAddModal(false);
      setFormData({ documentTitle: "", documentType: "", file: null });
      fetchDocuments();
    } catch {
      toast.error("Upload failed");
    }
  };

  const handleUpdateDocument = async () => {
    if (!editingDoc) return;

    const payload = new FormData();
    payload.append("DocumentTitle", editFormData.documentTitle);
    payload.append("DocumentType", editFormData.documentType);
    payload.append("OrgId", orgId);
    payload.append("ChangedBy", userId);

    if (editFormData.file) {
      payload.append("File", editFormData.file);
    }

    try {
      await updateHrOrgDocument(editingDoc.documentId, payload);
      toast.success("Document updated successfully");
      setShowEditModal(false);
      fetchDocuments();
    } catch {
      toast.error("Failed to update document");
    }
  };

  // SOFT DELETE - Move to trash
  const handleSoftDelete = async (doc) => {
    try {
      await deleteHrOrgDocument(doc.documentId);
      toast.success("Document moved to trash");
      setShowDeleteConfirmModal(false);
      setDocumentToDelete(null);
      fetchDocuments();
      fetchDeletedDocuments();
    } catch {
      toast.error("Failed to delete document");
    }
  };

  // RESTORE - Restore from trash
  const handleRestore = async (doc) => {
    try {
      await restoreHrOrgDocument(doc.documentId);
      toast.success("Document restored successfully");
      fetchDocuments();
      fetchDeletedDocuments();
    } catch {
      toast.error("Failed to restore document");
    }
  };

  // PERMANENT DELETE - Remove completely
  const handlePermanentDelete = async (doc) => {
    try {
      await permanentDeleteHrOrgDocument(doc.documentId);
      toast.success("Document permanently deleted");
      setShowDeleteConfirmModal(false);
      setDocumentToDelete(null);
      fetchDeletedDocuments();
    } catch {
      toast.error("Failed to permanently delete document");
    }
  };

  const openDeleteConfirmModal = (doc, type = "soft") => {
    setDocumentToDelete(doc);
    setDeleteType(type);
    setShowDeleteConfirmModal(true);
  };

  const openEditModal = (doc) => {
    setEditingDoc(doc);
    setEditFormData({
      documentTitle: doc.documentTitle,
      documentType: doc.documentType,
      file: null,
    });
    setShowEditModal(true);
  };

  // ================= VISIBILITY =================

  const loadVisibilityForEdit = async (doc) => {
    setSelectedDoc(doc);
    const v = { ...defaultVisibility };

    const rules = await getVisibilityByDocumentId(doc.documentId);

    rules.forEach((r) => {
      if (r.visibilityType === "EVERYONE") v.everyone = true;
      if (r.visibilityType === "ME") v.me = true;
      if (r.visibilityType === "DEPARTMENT") v.departments.push(r.referenceId);
      if (r.visibilityType === "USER") v.users.push(r.referenceId);
    });

    setVisibility(v);
    setShowVisibilityModal(true);
  };

  const saveVisibility = async () => {
    await saveHrOrgDocumentVisibility({
      documentId: selectedDoc.documentId,
      orgId,
      createdBy: userId,
      everyone: visibility.everyone,
      me: visibility.me,
      departmentIds: visibility.departments,
      userIds: visibility.users,
    });

    toast.success("Visibility updated");
    setShowVisibilityModal(false);
  };

  // ================= UI RENDER =================

  const renderDocumentTable = (docList, isDeletedView = false) => (
    <Table hover responsive className="align-middle">
      <thead className="table-light">
        <tr>
          <th>#</th>
          <th>Id</th>
          <th>Document</th>
          <th>Type</th>
          <th>Version</th>
          <th>Created</th>
          <th>Status</th>
          <th className="text-center">Action</th>
        </tr>
      </thead>
      <tbody>
        {docList.map((doc, i) => (
          <tr key={doc.documentId}>
            <td>{i + 1}</td>
            <td>{doc.documentId}</td>
            <td className="fw-semibold">{doc.documentTitle}</td>
            <td>
              <Badge bg="info">{documentTypeMap[doc.documentType]}</Badge>
            </td>
            <td>{doc.documentVersion}.0</td>
            <td>{moment(doc.createdOnDate).format("DD MMM YYYY")}</td>
            <td>
              {doc.active === false ? (
                <Badge bg="danger">Deleted</Badge>
              ) : (
                <Badge bg="success">Active</Badge>
              )}
            </td>
            <td className="text-center">
              <div
                style={{
                  display: "flex",
                  gap: "0.5rem",
                  justifyContent: "center",
                }}
              >
                {!isDeletedView ? (
                  <>
                    {/* ACTIVE DOCUMENT ACTIONS */}
                    {doc.createdBy === userId && (
                      <>
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Manage Visibility</Tooltip>}
                        >
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => loadVisibilityForEdit(doc)}
                            style={{ fontSize: "18px", padding: "2px 15px" }}
                          >
                            <FaUsersViewfinder />
                          </Button>
                        </OverlayTrigger>

                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Edit Document</Tooltip>}
                        >
                          <Button
                            size="sm"
                            variant="outline-warning"
                            onClick={() => openEditModal(doc)}
                            style={{ fontSize: "18px", padding: "2px 15px" }}
                          >
                            ✏️
                          </Button>
                        </OverlayTrigger>

                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Move to Trash</Tooltip>}
                        >
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => openDeleteConfirmModal(doc, "soft")}
                            style={{ fontSize: "18px", padding: "2px 15px" }}
                          >
                            <FaTrash />
                          </Button>
                        </OverlayTrigger>
                      </>
                    )}

                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>View Document</Tooltip>}
                    >
                      <Button
                        size="sm"
                        variant="outline-success"
                        onClick={() => viewHrOrgDocument(doc.documentId)}
                        style={{ fontSize: "18px", padding: "2px 15px" }}
                      >
                        <GrView />
                      </Button>
                    </OverlayTrigger>

                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>Version History</Tooltip>}
                    >
                      <Button
                        size="sm"
                        variant="outline-info"
                        onClick={() => openHistoryModal(doc)}
                        style={{ fontSize: "18px", padding: "2px 15px" }}
                      >
                        📜
                      </Button>
                    </OverlayTrigger>

                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>Download Document</Tooltip>}
                    >
                      <Button
                        size="sm"
                        variant="outline-dark"
                        onClick={() => downloadHrOrgDocument(doc.documentId)}
                        style={{ fontSize: "18px", padding: "2px 15px" }}
                      >
                        <FaDownload />
                      </Button>
                    </OverlayTrigger>
                  </>
                ) : (
                  <>
                    {/* DELETED DOCUMENT ACTIONS */}
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>Restore Document</Tooltip>}
                    >
                      <Button
                        size="sm"
                        variant="outline-success"
                        onClick={() => handleRestore(doc)}
                        style={{ fontSize: "11px", padding: "2px 15px" }}
                      >
                          {/* <FaTrashRestore /> */}
                          Restore
                      </Button>
                    </OverlayTrigger>

                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>Permanently Delete</Tooltip>}
                    >
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => openDeleteConfirmModal(doc, "permanent")}
                        style={{ fontSize: "18px", padding: "2px 15px" }}
                      >
                        <FaBan />
                      </Button>
                    </OverlayTrigger>

                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>View Document</Tooltip>}
                    >
                      <Button
                        size="sm"
                        variant="outline-success"
                        onClick={() => viewHrOrgDocument(doc.documentId)}
                        style={{ fontSize: "18px", padding: "2px 15px" }}
                      >
                        <GrView />
                      </Button>
                    </OverlayTrigger>

                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>Version History</Tooltip>}
                    >
                      <Button
                        size="sm"
                        variant="outline-info"
                        onClick={() => openHistoryModal(doc)}
                        style={{ fontSize: "18px", padding: "2px 15px" }}
                      >
                        📜
                      </Button>
                    </OverlayTrigger>

                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>Download Document</Tooltip>}
                    >
                      <Button
                        size="sm"
                        variant="outline-dark"
                        onClick={() => downloadHrOrgDocument(doc.documentId)}
                        style={{ fontSize: "18px", padding: "2px 15px" }}
                      >
                        <FaDownload />
                      </Button>
                    </OverlayTrigger>
                  </>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  return (
    <Container fluid className="main-content expand">
      <Card className="shadow-sm">
        <Card.Header className="bg-white">
          <Row className="align-items-center">
            <Col>
              <p style={{ fontSize: "25px" }} className="mb-0">
                📂 Policies
              </p>
              <small className="text-muted">
                Manage documents & visibility access
              </small>
            </Col>
            <Col className="text-end">
              <Button variant="primary" onClick={() => setShowAddModal(true)}>
                + Upload Document
              </Button>
            </Col>
          </Row>
        </Card.Header>

        <Card.Body>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-3"
          >
            <Tab
              eventKey="active"
              title={`Active Documents (${documents.length})`}
            >
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : documents.length ? (
                renderDocumentTable(documents, false)
              ) : (
                <Alert variant="info" className="text-center">
                  No active documents found
                </Alert>
              )}
            </Tab>

            <Tab
              eventKey="deleted"
              title={`Trash (${deletedDocuments.length})`}
            >
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : deletedDocuments.length ? (
                renderDocumentTable(deletedDocuments, true)
              ) : (
                <Alert variant="info" className="text-center">
                  No deleted documents found
                </Alert>
              )}
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>

      {/* ================= DELETE CONFIRMATION MODAL ================= */}
      <Modal
        show={showDeleteConfirmModal}
        onHide={() => setShowDeleteConfirmModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {deleteType === "soft" ? "Move to Trash" : "Permanently Delete"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteType === "soft" ? (
            <>
              <p>Are you sure you want to move this document to trash?</p>
              <p className="text-muted small">
                You can restore it later from the Trash section.
              </p>
            </>
          ) : (
            <>
              <p className="text-danger">⚠️ This action cannot be undone!</p>
              <p>
                Are you sure you want to permanently delete this document? It
                will be removed from both the database and storage completely.
              </p>
            </>
          )}
          <div className="mt-3">
            <strong>Document:</strong> {documentToDelete?.documentTitle}
            <br />
            <strong>Version:</strong> {documentToDelete?.documentVersion}.0
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteConfirmModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant={deleteType === "soft" ? "warning" : "danger"}
            onClick={() => {
              if (deleteType === "soft") {
                handleSoftDelete(documentToDelete);
              } else {
                handlePermanentDelete(documentToDelete);
              }
            }}
          >
            {deleteType === "soft" ? "Move to Trash" : "Permanently Delete"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ================= VISIBILITY MODAL ================= */}
      <Modal
        show={showVisibilityModal}
        onHide={() => setShowVisibilityModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>👁️ Visibility Settings</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Form.Check
              type="switch"
              label="Everyone"
              checked={visibility.everyone}
              onChange={(e) =>
                setVisibility({ ...visibility, everyone: e.target.checked })
              }
            />

            <Form.Check
              type="switch"
              label="Only Me"
              checked={visibility.me}
              onChange={(e) =>
                setVisibility({ ...visibility, me: e.target.checked })
              }
            />

            <hr />

            <Form.Label className="fw-semibold">Departments</Form.Label>
            <Form.Select
              multiple
              value={visibility.departments}
              onChange={(e) =>
                setVisibility({
                  ...visibility,
                  departments: Array.from(
                    e.target.selectedOptions,
                    (o) => o.value,
                  ),
                })
              }
            >
              {departments.map((d) => (
                <option key={d.deptCode} value={d.deptCode}>
                  {d.deptDescription}
                </option>
              ))}
            </Form.Select>

            <hr />

            <Form.Label className="fw-semibold">Specific Users</Form.Label>
            <Card className="border-0 shadow-sm mt-3">
              <Card.Body>
                <h6 className="fw-semibold mb-2">Specific Users</h6>
                <p className="text-muted small mb-3">
                  Select individual users who can view this document.
                </p>

                <Select
                  isMulti
                  options={userOptions}
                  placeholder="Search & select users..."
                  value={userOptions.filter((o) =>
                    visibility.users.includes(o.value),
                  )}
                  onChange={(selected) =>
                    setVisibility({
                      ...visibility,
                      users: selected ? selected.map((s) => s.value) : [],
                    })
                  }
                  classNamePrefix="react-select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: "44px",
                      borderRadius: "8px",
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: "#e7f1ff",
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: "#0d6efd",
                      fontWeight: 500,
                    }),
                  }}
                />

                {visibility.users.length > 0 && (
                  <div className="mt-3">
                    <small className="text-muted">Selected Users:</small>
                    <div className="mt-1">
                      {visibility.users.map((u) => (
                        <Badge key={u} bg="primary" className="me-1">
                          {u}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowVisibilityModal(false)}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={saveVisibility}>
            Save Visibility
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ================= ADD MODAL ================= */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>📤 Upload Document</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Document Title</Form.Label>
              <Form.Control
                value={formData.documentTitle}
                onChange={(e) =>
                  setFormData({ ...formData, documentTitle: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Document Type</Form.Label>
              <Form.Select
                value={formData.documentType}
                onChange={(e) =>
                  setFormData({ ...formData, documentType: e.target.value })
                }
              >
                <option value="">Select</option>
                {documentTypes.map((t) => (
                  <option key={t.documentTypeId} value={t.documentTypeId}>
                    {t.documentTypeName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group>
              <Form.Label>File</Form.Label>
              <Form.Control
                type="file"
                onChange={(e) =>
                  setFormData({ ...formData, file: e.target.files[0] })
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpload}>
            Upload
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ================= EDIT MODAL ================= */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>✏️ Update Document</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Document Title</Form.Label>
              <Form.Control
                value={editFormData.documentTitle}
                onChange={(e) =>
                  setEditFormData({
                    ...editFormData,
                    documentTitle: e.target.value,
                  })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Document Type</Form.Label>
              <Form.Select
                value={editFormData.documentType}
                onChange={(e) =>
                  setEditFormData({
                    ...editFormData,
                    documentType: e.target.value,
                  })
                }
              >
                <option value="">Select</option>
                {documentTypes.map((t) => (
                  <option key={t.documentTypeId} value={t.documentTypeId}>
                    {t.documentTypeName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group>
              <Form.Label>Upload New Version (Optional)</Form.Label>
              <Form.Control
                type="file"
                onChange={(e) =>
                  setEditFormData({
                    ...editFormData,
                    file: e.target.files[0],
                  })
                }
              />
              <small className="text-muted">
                Uploading a new file will create a new version
              </small>
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="warning" onClick={handleUpdateDocument}>
            Update
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ================= HISTORY MODAL ================= */}
      <Modal
        show={showHistoryModal}
        onHide={() => setShowHistoryModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>📜 Version History</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {historyList.length ? (
            <Table bordered hover>
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Created Date</th>
                  <th>Created By</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {historyList.map((h) => (
                  <tr key={h.documentId}>
                    <td>{h.documentVersion}.0</td>
                    <td>{moment(h.createdOnDate).format("DD MMM YYYY")}</td>
                    <td>{h.createdBy}</td>
                    <td>
                      {h.active === false ? (
                        <Badge bg="danger">Deleted</Badge>
                      ) : (
                        <Badge bg="success">Active</Badge>
                      )}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-success"
                        className="me-2"
                        onClick={() => viewHrOrgDocument(h.documentId)}
                      >
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-dark"
                        onClick={() => downloadHrOrgDocument(h.documentId)}
                      >
                        Download
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p>No version history found</p>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default ViewHrOrgDocuments;

// import { useEffect, useState } from "react";
// import {
//   Container,
//   Card,
//   Table,
//   Button,
//   Modal,
//   Form,
//   Spinner,
//   Alert,
//   Badge,
//   Row,
//   Col,
// } from "react-bootstrap";
// import { toast } from "react-toastify";
// import moment from "moment";
// import Select from "react-select";
// import { FaDownload } from "react-icons/fa6";
// import { GrView } from "react-icons/gr";
// import { FaUsersViewfinder } from "react-icons/fa6";
// import { OverlayTrigger, Tooltip } from "react-bootstrap";

// import {
//   uploadHrOrgDocument,
//   downloadHrOrgDocument,
//   getAllHrDocumentTypes,
//   saveHrOrgDocumentVisibility,
//   getVisibilityByDocumentId,
//   getVisibleHrOrgDocuments,
//   viewHrOrgDocument,
//   updateHrOrgDocument,
//   getDocumentHistory,
// } from "../Api/HrOrgDocumentMasterApi";

// import { getDepartmentsByOrgId } from "../Api/DepartmentOrgMaster";
// import { getResourceByMavUserId } from "../Api/ResourceList";
// import { getResourcesByOrgId } from "../Api/HrOrgDocumentMasterApi"; // ✅ NEW

// // ================= DEFAULT VISIBILITY =================
// const defaultVisibility = {
//   everyone: false,
//   me: false,
//   departments: [],
//   users: [], // ✅ NEW
// };

// const ViewHrOrgDocuments = () => {
//   const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//   const orgId = String(userDetails?.organisationId);
//   const userId = String(userDetails?.userId);

//   const [documents, setDocuments] = useState([]);
//   const [documentTypes, setDocumentTypes] = useState([]);
//   const [documentTypeMap, setDocumentTypeMap] = useState({});
//   const [departments, setDepartments] = useState([]);
//   const [resources, setResources] = useState([]); // ✅ NEW
//   const [userDepartmentIds, setUserDepartmentIds] = useState([]);
//   const [loading, setLoading] = useState(false);

//   const [showAddModal, setShowAddModal] = useState(false);
//   const [showVisibilityModal, setShowVisibilityModal] = useState(false);
//   const [selectedDoc, setSelectedDoc] = useState(null);

//   const [showHistoryModal, setShowHistoryModal] = useState(false);
//   const [historyList, setHistoryList] = useState([]);

//   const [formData, setFormData] = useState({
//     documentTitle: "",
//     documentType: "",
//     file: null,
//   });

//   const [visibility, setVisibility] = useState(defaultVisibility);

//   const [showEditModal, setShowEditModal] = useState(false);
//   const [editingDoc, setEditingDoc] = useState(null);
//   const [editFormData, setEditFormData] = useState({
//     documentTitle: "",
//     documentType: "",
//     file: null,
//   });

//   // ================= INIT =================
//   useEffect(() => {
//     if (orgId && userId) {
//       fetchUserResource();
//       fetchDepartments();
//       fetchDocumentTypes();
//       fetchResources(); // ✅ NEW
//     }
//   }, [orgId, userId]);

//   useEffect(() => {
//     fetchDocuments();
//   }, [userDepartmentIds]);

//   // ================= DATA =================

//   const fetchUserResource = async () => {
//     try {
//       const data = await getResourceByMavUserId(userId);
//       const deptCode = data?.resource?.department;
//       setUserDepartmentIds(deptCode ? [String(deptCode)] : []);
//     } catch {
//       setUserDepartmentIds([]);
//     }
//   };

//   const fetchDepartments = async () => {
//     const data = await getDepartmentsByOrgId(orgId);
//     setDepartments(data || []);
//   };

//   const fetchResources = async () => {
//     try {
//       const data = await getResourcesByOrgId(orgId);
//       setResources(data || []);
//     } catch {
//       toast.error("Failed to load users");
//     }
//   };

//   const userOptions = resources.map((u) => ({
//     value: u.resourceId, // stored in DB
//     label: `${u.name} (${u.resourceId})`, // shown in UI
//   }));

//   const fetchDocumentTypes = async () => {
//     const data = await getAllHrDocumentTypes();
//     setDocumentTypes(data || []);

//     const map = {};
//     data.forEach((t) => (map[t.documentTypeId] = t.documentTypeName));
//     setDocumentTypeMap(map);
//   };

//   const fetchDocuments = async () => {
//     try {
//       setLoading(true);
//       const data = await getVisibleHrOrgDocuments(
//         orgId,
//         userId,
//         userDepartmentIds,
//       );
// const latestDocs = getLatestVersions(data || []);
// setDocuments(latestDocs);
//     } catch {
//       toast.error("Failed to load documents");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const openHistoryModal = async (doc) => {
//     try {
//       const data = await getDocumentHistory(
//         doc.parentDocumentId || doc.documentId,
//       );
//       setHistoryList(data);
//       setShowHistoryModal(true);
//     } catch {
//       toast.error("Failed to load history");
//     }
//   };

//   // ================= UPLOAD =================

//   const handleUpload = async () => {
//     if (!formData.file) {
//       toast.error("Please select a file");
//       return;
//     }

//     const payload = new FormData();
//     payload.append("DocumentTitle", formData.documentTitle);
//     payload.append("DocumentType", formData.documentType);
//     payload.append("OrgId", orgId);
//     payload.append("CreatedBy", userId);
//     payload.append("File", formData.file);

//     try {
//       await uploadHrOrgDocument(payload);
//       toast.success("Document uploaded successfully");
//       setShowAddModal(false);
//       setFormData({ documentTitle: "", documentType: "", file: null });
//       fetchDocuments();
//     } catch {
//       toast.error("Upload failed");
//     }
//   };

//   const handleUpdateDocument = async () => {
//     if (!editingDoc) return;

//     const payload = new FormData();
//     payload.append("DocumentTitle", editFormData.documentTitle);
//     payload.append("DocumentType", editFormData.documentType);
//     payload.append("OrgId", orgId);
//     payload.append("UpdatedBy", userId);

//     if (editFormData.file) {
//       payload.append("File", editFormData.file); // optional new version
//     }

//     try {
//       await updateHrOrgDocument(editingDoc.documentId, payload);

//       toast.success("Document updated successfully");
//       setShowEditModal(false);
//       fetchDocuments();
//     } catch {
//       toast.error("Failed to update document");
//     }
//   };

//   const openEditModal = (doc) => {
//     setEditingDoc(doc);
//     setEditFormData({
//       documentTitle: doc.documentTitle,
//       documentType: doc.documentType,
//       file: null,
//     });
//     setShowEditModal(true);
//   };

//   const getLatestVersions = (docs) => {
//   const grouped = {};

//   docs.forEach((doc) => {
//     const rootId = doc.parentDocumentId || doc.documentId;

//     if (!grouped[rootId]) {const getLatestVersions = (docs) => {
//   const grouped = {};

//   docs.forEach((doc) => {
//     const rootId = doc.parentDocumentId || doc.documentId;

//     if (!grouped[rootId]) {
//       grouped[rootId] = doc;
//     } else if (doc.documentVersion > grouped[rootId].documentVersion) {
//       grouped[rootId] = doc;
//     }
//   });

//   return Object.values(grouped);
// };

//       grouped[rootId] = doc;
//     } else if (doc.documentVersion > grouped[rootId].documentVersion) {
//       grouped[rootId] = doc;
//     }
//   });

//   return Object.values(grouped);
// };

//   // ================= VISIBILITY =================

//   const loadVisibilityForEdit = async (doc) => {
//     setSelectedDoc(doc);
//     const v = { ...defaultVisibility };

//     const rules = await getVisibilityByDocumentId(doc.documentId);

//     rules.forEach((r) => {
//       if (r.visibilityType === "EVERYONE") v.everyone = true;
//       if (r.visibilityType === "ME") v.me = true;
//       if (r.visibilityType === "DEPARTMENT") v.departments.push(r.referenceId);
//       if (r.visibilityType === "USER") v.users.push(r.referenceId); // ✅
//     });

//     setVisibility(v);
//     setShowVisibilityModal(true);
//   };

//   const saveVisibility = async () => {
//     await saveHrOrgDocumentVisibility({
//       documentId: selectedDoc.documentId,
//       orgId,
//       createdBy: userId,
//       everyone: visibility.everyone,
//       me: visibility.me,
//       departmentIds: visibility.departments,
//       userIds: visibility.users, // ✅
//     });

//     toast.success("Visibility updated");
//     setShowVisibilityModal(false);
//   };

//   // ================= UI =================

//   return (
//     <Container fluid className="main-content expand">
//       <Card className="shadow-sm">
//         <Card.Header className="bg-white">
//           <Row className="align-items-center">
//             <Col>
//               <p style={{ fontSize: "25px" }} className="mb-0">
//                 📂 Policies
//               </p>
//               <small className="text-muted">
//                 Manage documents & visibility access
//               </small>
//             </Col>
//             <Col
//               className="text-end"
//               style={{ display: "flex", justifyContent: "flex-end" }}
//             >
//               <Button variant="primary" onClick={() => setShowAddModal(true)}>
//                 + Upload Document
//               </Button>
//             </Col>
//           </Row>
//         </Card.Header>

//         <Card.Body>
//           {loading ? (
//             <div className="text-center py-5">
//               <Spinner animation="border" />
//             </div>
//           ) : documents.length ? (
//             <Table hover responsive className="align-middle">
//               <thead className="table-light">
//                 <tr>
//                   <th>#</th>
//                   <th>Id</th>
//                   <th>Document</th>
//                   <th>Type</th>
//                   <th>Version</th>
//                   <th>Created</th>
//                   <th>Visibility</th>
//                   <th className="text-center">Action</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {documents.map((doc, i) => (
//                   <tr key={doc.documentId}>
//                     <td>{i + 1}</td>
//                     <td>{doc.documentId}</td>
//                     <td className="fw-semibold">{doc.documentTitle}</td>
//                     <td>
//                       <Badge bg="info">
//                         {documentTypeMap[doc.documentType]}
//                       </Badge>
//                     </td>
//                     <td>{doc.documentVersion}.0</td>
//                     <td>{moment(doc.createdOnDate).format("DD MMM YYYY")}</td>
//                     <td>
//                       {doc.createdBy === userId ? (
//                         <Badge bg="success">Owner</Badge>
//                       ) : (
//                         <Badge bg="secondary">Restricted</Badge>
//                       )}
//                     </td>
//                     {/* <td
//                       className="text-center"
//                       style={{
//                         display: "flex",
//                         gap: "0.5rem",
//                         justifyContent: "center",
//                       }}

//                     >
//                       {doc.createdBy === userId && (
//                         <Button
//                           size="sm"
//                           variant="outline-primary"
//                           className="me-2"
//                           onClick={() => loadVisibilityForEdit(doc)}
//                         >
//                           Visibility
//                         </Button>
//                       )}
//                       <Button
//                         size="sm"
//                         variant="outline-dark"
//                         onClick={() => downloadHrOrgDocument(doc.documentId)}
//                       >
//                         Download
//                       </Button>
//                     </td> */}
//                     <td
//                       className="text-center"
//                       style={{
//                         display: "flex",
//                         gap: "0.5rem",
//                         justifyContent: "center",
//                       }}
//                     >
//                       {/* VISIBILITY */}
//                       {doc.createdBy === userId && (
//                         <OverlayTrigger
//                           placement="top"
//                           overlay={<Tooltip>Manage Visibility</Tooltip>}
//                         >
//                           <Button
//                             size="sm"
//                             variant="outline-primary"
//                             onClick={() => loadVisibilityForEdit(doc)}
//                             style={{ fontSize: "18px", padding: "2px 15px" }}
//                           >
//                             <FaUsersViewfinder />
//                           </Button>
//                         </OverlayTrigger>
//                       )}

//                       {/* EDIT */}
//                       {doc.createdBy === userId && (
//                         <OverlayTrigger
//                           placement="top"
//                           overlay={<Tooltip>Edit Document</Tooltip>}
//                         >
//                           <Button
//                             size="sm"
//                             variant="outline-warning"
//                             onClick={() => openEditModal(doc)}
//                             style={{ fontSize: "18px", padding: "2px 15px" }}
//                           >
//                             ✏️
//                           </Button>
//                         </OverlayTrigger>
//                       )}

//                       {/* VIEW */}
//                       <OverlayTrigger
//                         placement="top"
//                         overlay={<Tooltip>View Document</Tooltip>}
//                       >
//                         <Button
//                           size="sm"
//                           variant="outline-success"
//                           onClick={() => viewHrOrgDocument(doc.documentId)}
//                           style={{ fontSize: "18px", padding: "2px 15px" }}
//                         >
//                           <GrView />
//                         </Button>
//                       </OverlayTrigger>

//                       {/* HISTORY */}
//                       <OverlayTrigger
//                         placement="top"
//                         overlay={<Tooltip>Version History</Tooltip>}
//                       >
//                         <Button
//                           size="sm"
//                           variant="outline-info"
//                           onClick={() => openHistoryModal(doc)}
//                           style={{ fontSize: "18px", padding: "2px 15px" }}
//                         >
//                           📜
//                         </Button>
//                       </OverlayTrigger>

//                       {/* DOWNLOAD */}
//                       <OverlayTrigger
//                         placement="top"
//                         overlay={<Tooltip>Download Document</Tooltip>}
//                       >
//                         <Button
//                           size="sm"
//                           variant="outline-dark"
//                           onClick={() => downloadHrOrgDocument(doc.documentId)}
//                           style={{ fontSize: "18px", padding: "2px 15px" }}
//                         >
//                           <FaDownload />
//                         </Button>
//                       </OverlayTrigger>
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </Table>
//           ) : (
//             <Alert variant="info" className="text-center">
//               No documents found
//             </Alert>
//           )}
//         </Card.Body>
//       </Card>

//       {/* ================= VISIBILITY MODAL ================= */}

//       <Modal
//         show={showVisibilityModal}
//         onHide={() => setShowVisibilityModal(false)}
//         size="lg"
//         centered
//       >
//         <Modal.Header closeButton>
//           <Modal.Title>👁️ Visibility Settings</Modal.Title>
//         </Modal.Header>

//         <Modal.Body>
//           <Form>
//             {/* GENERAL */}
//             <Form.Check
//               type="switch"
//               label="Everyone"
//               checked={visibility.everyone}
//               onChange={(e) =>
//                 setVisibility({ ...visibility, everyone: e.target.checked })
//               }
//             />

//             <Form.Check
//               type="switch"
//               label="Only Me"
//               checked={visibility.me}
//               onChange={(e) =>
//                 setVisibility({ ...visibility, me: e.target.checked })
//               }
//             />

//             <hr />

//             {/* DEPARTMENTS */}
//             <Form.Label className="fw-semibold">Departments</Form.Label>
//             <Form.Select
//               multiple
//               value={visibility.departments}
//               onChange={(e) =>
//                 setVisibility({
//                   ...visibility,
//                   departments: Array.from(
//                     e.target.selectedOptions,
//                     (o) => o.value,
//                   ),
//                 })
//               }
//             >
//               {departments.map((d) => (
//                 <option key={d.deptCode} value={d.deptCode}>
//                   {d.deptDescription}
//                 </option>
//               ))}
//             </Form.Select>

//             <hr />

//             {/* USERS */}
//             <Form.Label className="fw-semibold">Specific Users</Form.Label>
//             {/* ===== USER VISIBILITY ===== */}
//             <Card className="border-0 shadow-sm mt-3">
//               <Card.Body>
//                 <h6 className="fw-semibold mb-2">Specific Users</h6>
//                 <p className="text-muted small mb-3">
//                   Select individual users who can view this document.
//                 </p>

//                 <Select
//                   isMulti
//                   options={userOptions}
//                   placeholder="Search & select users..."
//                   value={userOptions.filter((o) =>
//                     visibility.users.includes(o.value),
//                   )}
//                   onChange={(selected) =>
//                     setVisibility({
//                       ...visibility,
//                       users: selected ? selected.map((s) => s.value) : [],
//                     })
//                   }
//                   classNamePrefix="react-select"
//                   styles={{
//                     control: (base) => ({
//                       ...base,
//                       minHeight: "44px",
//                       borderRadius: "8px",
//                     }),
//                     multiValue: (base) => ({
//                       ...base,
//                       backgroundColor: "#e7f1ff",
//                     }),
//                     multiValueLabel: (base) => ({
//                       ...base,
//                       color: "#0d6efd",
//                       fontWeight: 500,
//                     }),
//                   }}
//                 />

//                 {visibility.users.length > 0 && (
//                   <div className="mt-3">
//                     <small className="text-muted">Selected Users:</small>
//                     <div className="mt-1">
//                       {visibility.users.map((u) => (
//                         <Badge key={u} bg="primary" className="me-1">
//                           {u}
//                         </Badge>
//                       ))}
//                     </div>
//                   </div>
//                 )}
//               </Card.Body>
//             </Card>
//           </Form>
//         </Modal.Body>

//         <Modal.Footer>
//           <Button
//             variant="secondary"
//             onClick={() => setShowVisibilityModal(false)}
//           >
//             Cancel
//           </Button>
//           <Button variant="primary" onClick={saveVisibility}>
//             Save Visibility
//           </Button>
//         </Modal.Footer>
//       </Modal>

//       <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
//         <Modal.Header closeButton>
//           <Modal.Title>📤 Upload Document</Modal.Title>
//         </Modal.Header>

//         <Modal.Body>
//           <Form>
//             <Form.Group className="mb-3">
//               <Form.Label>Document Title</Form.Label>
//               <Form.Control
//                 value={formData.documentTitle}
//                 onChange={(e) =>
//                   setFormData({ ...formData, documentTitle: e.target.value })
//                 }
//               />
//             </Form.Group>

//             <Form.Group className="mb-3">
//               <Form.Label>Document Type</Form.Label>
//               <Form.Select
//                 value={formData.documentType}
//                 onChange={(e) =>
//                   setFormData({ ...formData, documentType: e.target.value })
//                 }
//               >
//                 <option value="">Select</option>
//                 {documentTypes.map((t) => (
//                   <option key={t.documentTypeId} value={t.documentTypeId}>
//                     {t.documentTypeName}
//                   </option>
//                 ))}
//               </Form.Select>
//             </Form.Group>

//             <Form.Group>
//               <Form.Label>File</Form.Label>
//               <Form.Control
//                 type="file"
//                 onChange={(e) =>
//                   setFormData({ ...formData, file: e.target.files[0] })
//                 }
//               />
//             </Form.Group>
//           </Form>
//         </Modal.Body>

//         <Modal.Footer>
//           <Button variant="secondary" onClick={() => setShowAddModal(false)}>
//             Cancel
//           </Button>
//           <Button variant="primary" onClick={handleUpload}>
//             Upload
//           </Button>
//         </Modal.Footer>
//       </Modal>

//       <Modal
//         show={showEditModal}
//         onHide={() => setShowEditModal(false)}
//         centered
//       >
//         <Modal.Header closeButton>
//           <Modal.Title>✏️ Update Document</Modal.Title>
//         </Modal.Header>

//         <Modal.Body>
//           <Form>
//             <Form.Group className="mb-3">
//               <Form.Label>Document Title</Form.Label>
//               <Form.Control
//                 value={editFormData.documentTitle}
//                 onChange={(e) =>
//                   setEditFormData({
//                     ...editFormData,
//                     documentTitle: e.target.value,
//                   })
//                 }
//               />
//             </Form.Group>

//             <Form.Group className="mb-3">
//               <Form.Label>Document Type</Form.Label>
//               <Form.Select
//                 value={editFormData.documentType}
//                 onChange={(e) =>
//                   setEditFormData({
//                     ...editFormData,
//                     documentType: e.target.value,
//                   })
//                 }
//               >
//                 <option value="">Select</option>
//                 {documentTypes.map((t) => (
//                   <option key={t.documentTypeId} value={t.documentTypeId}>
//                     {t.documentTypeName}
//                   </option>
//                 ))}
//               </Form.Select>
//             </Form.Group>

//             <Form.Group>
//               <Form.Label>Upload New Version (Optional)</Form.Label>
//               <Form.Control
//                 type="file"
//                 onChange={(e) =>
//                   setEditFormData({
//                     ...editFormData,
//                     file: e.target.files[0],
//                   })
//                 }
//               />
//             </Form.Group>
//           </Form>
//         </Modal.Body>

//         <Modal.Footer>
//           <Button variant="secondary" onClick={() => setShowEditModal(false)}>
//             Cancel
//           </Button>
//           <Button variant="warning" onClick={handleUpdateDocument}>
//             Update
//           </Button>
//         </Modal.Footer>
//       </Modal>
//       <Modal
//         show={showHistoryModal}
//         onHide={() => setShowHistoryModal(false)}
//         size="lg"
//         centered
//       >
//         <Modal.Header closeButton>
//           <Modal.Title>📜 Version History</Modal.Title>
//         </Modal.Header>

//         <Modal.Body>
//           {historyList.length ? (
//             <Table bordered hover>
//               <thead>
//                 <tr>
//                   <th>Version</th>
//                   <th>Created Date</th>
//                   <th>Created By</th>
//                   <th>Action</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {historyList.map((h) => (
//                   <tr key={h.documentId}>
//                     <td>{h.documentVersion}.0</td>
//                     <td>{moment(h.createdOnDate).format("DD MMM YYYY")}</td>
//                     <td>{h.createdBy}</td>
//                     <td>
//                       <Button
//                         size="sm"
//                         variant="outline-success"
//                         onClick={() => viewHrOrgDocument(h.documentId)}
//                       >
//                         View
//                       </Button>{" "}
//                       <Button
//                         size="sm"
//                         variant="outline-dark"
//                         onClick={() => downloadHrOrgDocument(h.documentId)}
//                       >
//                         Download
//                       </Button>
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </Table>
//           ) : (
//             <p>No version history found</p>
//           )}
//         </Modal.Body>
//       </Modal>
//     </Container>
//   );
// };

// export default ViewHrOrgDocuments;
